import type { ErrorEvent } from "@sentry/types";

/**
 * Filters Sentry error events to include only unhandled exceptions.
 *
 * By default, exceptions sent using `captureException` are marked as handled.
 * This function ensures that only unhandled exceptions or those explicitly tagged
 * as unhandled are sent to Sentry.
 *
 * This means that events from captureException will be ignored, use logger.error
 * instead.
 *
 * @param event - The error event received from Sentry.
 * @returns The unhandled error event, or `null` if the event is handled.
 */
export function getOnlyUnhandledExceptions(event: ErrorEvent): ErrorEvent | null {
  const isUnhandled = event.tags?.unhandled ?? false;

  if (isUnhandled) {
    return event;
  }

  const exceptions = event.exception?.values ?? [];

  if (exceptions.length > 0) {
    const exc = exceptions[exceptions.length - 1];
    const mechanism = exc.mechanism;

    if (mechanism?.handled) {
      return null;
    }
  }

  return event;
}
